// client/src/components/ProductImageGallery.js
import React, { useRef, useState, useEffect } from 'react';
import { Box, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { getImageUrl } from '../utils/media';

// Zooms the active slide toward wherever the cursor is (desktop hover only).
const ZoomableSlide = ({ src, alt }) => {
  const ref = useRef(null);
  const [origin, setOrigin] = useState('center center');
  const [zoomed, setZoomed] = useState(false);

  const handleMouseMove = (e) => {
    const rect = ref.current.getBoundingClientRect();
    setOrigin(`${((e.clientX - rect.left) / rect.width) * 100}% ${((e.clientY - rect.top) / rect.height) * 100}%`);
  };

  return (
    <Box
      ref={ref}
      onMouseEnter={() => setZoomed(true)}
      onMouseLeave={() => setZoomed(false)}
      onMouseMove={handleMouseMove}
      sx={{
        overflow: 'hidden', cursor: 'zoom-in', width: '100%', height: '100%', flexShrink: 0,
        scrollSnapAlign: 'start', bgcolor: 'background.paper',
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          width: '100%', height: '100%', display: 'block', objectFit: 'contain',
          transition: 'transform 0.2s ease-out',
          transform: zoomed ? 'scale(1.8)' : 'scale(1)',
          transformOrigin: origin,
        }}
      />
    </Box>
  );
};

const ProductImageGallery = ({ images, alt }) => {
  const trackRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const scrollToIndex = (index) => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({ left: index * track.clientWidth, behavior: 'smooth' });
  };

  const handleScroll = () => {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) return;
    setActiveIndex(Math.round(track.scrollLeft / track.clientWidth));
  };

  useEffect(() => {
    setActiveIndex(0);
    trackRef.current?.scrollTo({ left: 0 });
  }, [images]);

  if (!images || images.length === 0) return null;

  return (
    <Box>
      <Box sx={{ position: 'relative', borderRadius: 2, overflow: 'hidden' }}>
        <Box
          ref={trackRef}
          onScroll={handleScroll}
          sx={{
            display: 'flex', overflowX: 'auto', scrollSnapType: 'x mandatory',
            height: 420, scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {images.map((img, i) => (
            <ZoomableSlide key={img.id ?? i} src={getImageUrl(img.image_url)} alt={alt} />
          ))}
        </Box>

        {images.length > 1 && (
          <>
            <IconButton
              onClick={() => scrollToIndex(Math.max(0, activeIndex - 1))}
              sx={{ position: 'absolute', top: '50%', left: 8, transform: 'translateY(-50%)', bgcolor: 'background.paper', display: { xs: 'none', sm: 'inline-flex' }, '&:hover': { bgcolor: 'background.paper' } }}
              size="small"
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => scrollToIndex(Math.min(images.length - 1, activeIndex + 1))}
              sx={{ position: 'absolute', top: '50%', right: 8, transform: 'translateY(-50%)', bgcolor: 'background.paper', display: { xs: 'none', sm: 'inline-flex' }, '&:hover': { bgcolor: 'background.paper' } }}
              size="small"
            >
              <ChevronRightIcon />
            </IconButton>

            {/* Fraction indicator */}
            <Box sx={{ position: 'absolute', bottom: 10, right: 10, bgcolor: 'rgba(0,0,0,0.6)', color: '#fff', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.75rem' }}>
              {activeIndex + 1} / {images.length}
            </Box>

            {/* Dot indicators */}
            <Box sx={{ position: 'absolute', bottom: 10, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 0.75 }}>
              {images.map((img, i) => (
                <Box
                  key={img.id ?? i}
                  onClick={() => scrollToIndex(i)}
                  sx={{
                    width: 7, height: 7, borderRadius: '50%', cursor: 'pointer',
                    bgcolor: i === activeIndex ? '#fff' : 'rgba(255,255,255,0.5)',
                  }}
                />
              ))}
            </Box>
          </>
        )}
      </Box>

      {images.length > 1 && (
        // A horizontally-scrolling strip of fixed-size thumbnails, rather than a
        // fixed-column grid — a rigid column count either squeezes every thumb
        // razor-thin on a narrow screen or wraps awkwardly once there are more
        // photos than columns. Fixed size + scroll scales to any screen/count.
        <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', mt: 1.5, pb: 0.5 }}>
          {images.map((img, i) => (
            <Box
              key={img.id ?? i}
              onClick={() => scrollToIndex(i)}
              sx={{
                cursor: 'pointer', borderRadius: 1, overflow: 'hidden', border: '2px solid',
                borderColor: activeIndex === i ? 'primary.main' : 'transparent',
                opacity: activeIndex === i ? 1 : 0.7, '&:hover': { opacity: 1 },
                flexShrink: 0, width: 64, height: 64,
              }}
            >
              <Box component="img" src={getImageUrl(img.image_url)} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ProductImageGallery;
